import React, { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import Button from '@shared/atoms/Button';
import Tooltip from '@shared/atoms/Tooltip';
import T from '@shared/atoms/Typography';
import FormAutocompleteWithList from '@shared/atoms/FormFields/FormAutocompleteWithList';
import FormInput from '@shared/atoms/FormFields/FormInput';
import FormSelector from '@shared/atoms/FormFields/FormSelector';

import {
  courseType,
  AUTOCOMPLETEWITHLIST_TYPE,
  courseStatus,
} from '@shared/const';
import { newSelectTranslation, useForm } from '@shared/utils';
import theme from '@shared/themes/default';

import ValidationList from '../../components/ValidationList';

import {
  COURSE_INTEGRITY,
  DEFAULT_EXAM_TIME_PER_QUESTION,
  getExamTimeLimitToSubmit,
  shouldPrefillExamTime,
} from './integrity';

import { useAppGlobals } from '../../../../contexts/AppContext';
import { useCourse } from '../../../../contexts/CourseContext';

import { listCatalogs } from './graphql/queries';

const SectionWrapper = styled.section`
  background: ${theme.palette.darkWrapper};
  padding: ${theme.spacing.s};
`;

const ContentWrapper = styled.section`
  background: ${theme.palette.darkDistinct};
  padding: ${theme.spacing.s} ${theme.spacing.s} ${theme.spacing.xl};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.s};
`;

const HeaderWrapper = styled.div`
  display: flex;
  justify-content: end;
  align-items: center;
  margin: ${theme.spacing.s};
`;

const CatalogWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.s};
  width: 418px;
`;

const NarrowFieldWrapper = styled.div`
  width: 418px;
  max-width: 100%;
`;

const getType = ({ everyone, paid, logo }) => {
  if (everyone) {
    if (paid) {
      return courseType.PREMIUM;
    }
    return courseType.FREE;
  }
  if (logo) {
    return courseType.PREMIUM;
  }
  return courseType.SELFPUBLISH;
};

const Access = () => {
  const { t } = useTranslation();
  const { videocationCatalog } = useAppGlobals();
  const {
    canPublish,
    completionIntegrityFlagEnabled,
    course,
    hasChanged,
    hasError,
    locked,
    saveAccess,
    setHandleSave,
    setHasChanged,
    submitting,
    validationDetails,
  } = useCourse();

  const PAID_OPTIONS = {
    FREE: {
      key: 'free',
      value: t('backoffice.courses.accessTab.free'),
    },
    PAID: {
      key: 'paid',
      value: t('backoffice.courses.accessTab.paid'),
    },
  };
  const LOGO_OPTIONS = {
    YES: {
      key: 'yes',
      value: t('backoffice.courses.accessTab.yes'),
    },
    NO: {
      key: 'no',
      value: t('backoffice.courses.accessTab.no'),
    },
  };

  const INTEGRITY_OPTIONS = [
    {
      key: COURSE_INTEGRITY.NONE,
      value: t('backoffice.courses.detailsTab.integrityNone'),
    },
    {
      key: COURSE_INTEGRITY.MID,
      value: t('backoffice.courses.detailsTab.integrityMid'),
    },
    {
      key: COURSE_INTEGRITY.STRICT,
      value: t('backoffice.courses.detailsTab.integrityStrict'),
    },
  ];

  const onSubmit = ({
    catalogs,
    paid,
    logo,
    integrity,
    examTimeLimitSeconds,
  }) => {
    saveAccess({
      catalogs,
      type: getType({
        everyone: catalogs.some((catId) => catId === videocationCatalog.id),
        paid: paid === PAID_OPTIONS.PAID.key,
        logo: logo === LOGO_OPTIONS.YES.key,
      }),
      integrity,
      examTimeLimitSeconds: getExamTimeLimitToSubmit(
        integrity,
        examTimeLimitSeconds
      ),
    });
  };

  const {
    disableSubmit,
    edited,
    handleChange,
    handleSubmit,
    properties,
    setDefaults,
    values,
  } = useForm({
    changesRequired: true,
    fields: {
      catalogs: { required: true },
      paid: { required: true },
      logo: { required: true },
      integrity: {},
      examTimeLimitSeconds: {
        mode: 'numeric',
        pattern: '[0-9]*',
        validation: (value) => value === '' || /^[0-9]+$/.test(String(value)),
      },
    },
    onSubmit,
  });

  const { useNewGraphQLQuery } = useAppGlobals();
  const listCatalogsResponse = useNewGraphQLQuery(listCatalogs, {
    variables: {
      pagination: { limit: 1000 },
    },
  });

  const handleAddCatalog = (catalogId) => {
    handleChange({
      name: 'catalogs',
      value: [...(values.catalogs || []), catalogId],
    });
  };

  const handleDeleteCatalog = (catalogId) => {
    handleChange({
      name: 'catalogs',
      value: [...(values.catalogs || [])].filter(
        (catId) => catId !== catalogId
      ),
    });
  };

  // Keep the seconds field digits-only so an invalid value can never be
  // entered (which would otherwise silently block saving, even after
  // switching away from strict).
  const handleExamTimeChange = ({ name, value }) => {
    handleChange({ name, value: String(value).replace(/[^0-9]/g, '') });
  };

  useEffect(() => {
    if (course) {
      setDefaults({
        catalogs: course.catalogs?.length
          ? course.catalogs.map((cat) => cat.id)
          : [],
        paid:
          course.type !== courseType.FREE
            ? PAID_OPTIONS.PAID.key
            : PAID_OPTIONS.FREE.key,
        logo:
          course.type !== courseType.SELFPUBLISH
            ? LOGO_OPTIONS.YES.key
            : LOGO_OPTIONS.NO.key,
        integrity: course.integrity || COURSE_INTEGRITY.NONE,
        examTimeLimitSeconds:
          course.examTimeLimitSeconds != null
            ? String(course.examTimeLimitSeconds)
            : '0',
      });
    }
  }, [course]);

  useEffect(() => {
    setHandleSave(() => handleSubmit);
  }, [values, disableSubmit]);

  useEffect(() => {
    setHasChanged(edited);
  }, [edited]);

  useEffect(() => {
    if (
      values.catalogs?.length &&
      values.paid === PAID_OPTIONS.FREE.key &&
      (values.catalogs?.length !== 1 ||
        !values.catalogs.some((id) => id === videocationCatalog.id))
    ) {
      handleChange({ name: 'paid', value: PAID_OPTIONS.PAID.key });
    }
  }, [values.catalogs, values.paid, videocationCatalog]);

  useEffect(() => {
    if (
      values.catalogs?.length &&
      values.logo === LOGO_OPTIONS.NO.key &&
      values.catalogs.some((id) => id === videocationCatalog.id)
    ) {
      handleChange({ name: 'logo', value: LOGO_OPTIONS.YES.key });
    }
  }, [values.catalogs, values.logo, videocationCatalog]);

  // When integrity becomes strict without a stored positive time limit,
  // prefill the default of 60 seconds per question.
  useEffect(() => {
    if (shouldPrefillExamTime(values.integrity, values.examTimeLimitSeconds)) {
      handleChange({
        name: 'examTimeLimitSeconds',
        value: DEFAULT_EXAM_TIME_PER_QUESTION,
      });
    }
  }, [values.integrity]);

  const allCatalogs = listCatalogsResponse.data?.listCatalogs || [];
  const catalogs = allCatalogs.filter((catalog) => {
    if (!catalog?.organization) {
      return true;
    }

    if (catalog?.organization?.archived) {
      return false;
    }

    if (
      !catalog.organization?.subscription ||
      catalog.organization?.subscription?.status !== 'active'
    ) {
      return false;
    }

    return true;
  });
  const selectedCatalogs = catalogs.reduce((acc, curr) => {
    if (values.catalogs?.length && values.catalogs.includes(curr.id)) {
      acc.push({
        key: curr.id,
        title: newSelectTranslation(curr.name),
      });
    }
    return acc;
  }, []);

  const sortCatalogItems = useMemo(
    () =>
      (items = []) => {
        const videocationItem = items.find(
          (items) => items.key === videocationCatalog?.id
        );
        const otherItems = items
          .filter((item) => item.key !== videocationCatalog?.id)
          .sort((a, b) => {
            return a.title?.localeCompare(b.title);
          });
        if (videocationItem) {
          return [videocationItem, ...otherItems];
        }
        return items;
      },
    [videocationCatalog]
  );

  const needsExperts = (values.catalogs || []).some(
    (catId) => catId === videocationCatalog.id
  );

  const disableSave =
    locked ||
    !hasChanged ||
    hasError ||
    !selectedCatalogs.length ||
    (course?.status === courseStatus.PUBLISHED && !canPublish) ||
    (needsExperts &&
      !course.experts?.length &&
      course.status === courseStatus.PUBLISHED);

  const paidDisabled =
    values.catalogs?.length !== 1 ||
    !values.catalogs.some((id) => id === videocationCatalog.id);
  const logoDisabled =
    values.catalogs?.length === 0 ||
    (values.catalogs?.length &&
      values.catalogs.some((id) => id === videocationCatalog.id));

  // `course.integrity` is a nullable GraphQL field (`CourseIntegrity`, no `!`),
  // so it can be `null` for courses that never had a value stored. Without
  // `?? COURSE_INTEGRITY.NONE`, `null !== COURSE_INTEGRITY.NONE` would be true
  // and every never-touched course would show the selector with the flag off.
  const completionIntegrityVisible =
    completionIntegrityFlagEnabled ||
    (course.integrity ?? COURSE_INTEGRITY.NONE) !== COURSE_INTEGRITY.NONE;
  const completionIntegrityDisabled =
    locked || submitting || !completionIntegrityFlagEnabled;

  return (
    <SectionWrapper>
      <HeaderWrapper>
        <Tooltip
          arrow
          title={
            course.status === courseStatus.PUBLISHED &&
            disableSave &&
            !submitting &&
            !locked &&
            (Object.keys(validationDetails)?.length ||
              (needsExperts && !course.experts?.length)) ? (
              <>
                <T color={theme.palette.mainDark}>
                  {t('backoffice.courses.accessTab.missingExperts')}
                </T>
                {Object.keys(validationDetails || {}).length > 0 && (
                  <ValidationList details={validationDetails} />
                )}
              </>
            ) : (
              ''
            )
          }
        >
          <div>
            <Button
              disabled={disableSave}
              onClick={handleSubmit}
              loading={submitting}
              type="secondary"
            >
              {t('generic.save')}
            </Button>
          </div>
        </Tooltip>
      </HeaderWrapper>
      <ContentWrapper>
        <CatalogWrapper>
          <FormAutocompleteWithList
            id="catalogs"
            name="catalogs"
            filterOptions
            disabled={listCatalogsResponse.loading || locked || submitting}
            items={catalogs.map((cat) => ({
              key: cat.id,
              title: newSelectTranslation(cat.name),
            }))}
            infoText={t('backoffice.courses.accessTab.courseCatalogsTooltip')}
            label={t('backoffice.courses.accessTab.courseCatalogs')}
            loading={listCatalogsResponse.loading}
            loadingText={t('generic.loadingText')}
            noOptionsText={t('generic.noSearchResults')}
            onAdd={handleAddCatalog}
            onDelete={handleDeleteCatalog}
            selectedItems={selectedCatalogs}
            sortHandler={sortCatalogItems}
            properties={properties}
            theme="dark"
            type={AUTOCOMPLETEWITHLIST_TYPE.FORM}
          />
        </CatalogWrapper>
        <div>
          <FormSelector
            id="paid"
            name="paid"
            label={t('backoffice.courses.accessTab.courseAccess')}
            infoText={t('backoffice.courses.accessTab.courseAccessTooltip')}
            disabled={
              listCatalogsResponse.loading ||
              locked ||
              submitting ||
              paidDisabled
            }
            handleChange={handleChange}
            options={Object.values(PAID_OPTIONS)}
            properties={properties}
            values={values}
          />
        </div>
        <div>
          <FormSelector
            id="logo"
            name="logo"
            label={t('backoffice.courses.accessTab.videocationLogo')}
            infoText={t('backoffice.courses.accessTab.videocationLogoTooltip')}
            disabled={
              listCatalogsResponse.loading ||
              locked ||
              submitting ||
              logoDisabled
            }
            handleChange={handleChange}
            options={Object.values(LOGO_OPTIONS)}
            properties={properties}
            values={values}
          />
        </div>
        {completionIntegrityVisible && (
          <div>
            <FormSelector
              id="integrity"
              name="integrity"
              label={t('backoffice.courses.detailsTab.completionIntegrity')}
              infoText={t(
                'backoffice.courses.detailsTab.completionIntegrityTooltip'
              )}
              disabled={completionIntegrityDisabled}
              handleChange={handleChange}
              options={INTEGRITY_OPTIONS}
              properties={properties}
              values={values}
            />
          </div>
        )}
        {completionIntegrityVisible &&
          values.integrity === COURSE_INTEGRITY.STRICT && (
            <NarrowFieldWrapper>
              <FormInput
                id="examTimeLimitSeconds"
                name="examTimeLimitSeconds"
                label={t('backoffice.courses.detailsTab.examTimePerQuestion')}
                infoText={t(
                  'backoffice.courses.detailsTab.examTimePerQuestionTooltip'
                )}
                placeholder={t(
                  'backoffice.courses.detailsTab.examTimePerQuestionPlaceholder'
                )}
                disabled={completionIntegrityDisabled}
                handleChange={handleExamTimeChange}
                properties={properties}
                values={values}
              />
            </NarrowFieldWrapper>
          )}
      </ContentWrapper>
    </SectionWrapper>
  );
};

export default Access;
